import React, { useEffect, useReducer, useState } from "react";
import api from "../../services/api";

import {
  Button,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Tooltip,
} from "@material-ui/core";

import DescriptionIcon from "@material-ui/icons/Description";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD":
      return action.payload;
    case "RESET":
      return [];
    default:
      return state;
  }
};

const Comprobantes = () => {
  const classes = useStyles();

  const [data, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // 🔍 filtros
  const [search, setSearch] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("TODOS");

  // 📅 filtros DOBLES
  const [fechaRegDesde, setFechaRegDesde] = useState("");
  const [fechaRegHasta, setFechaRegHasta] = useState("");

  const [fechaCompDesde, setFechaCompDesde] = useState("");
  const [fechaCompHasta, setFechaCompHasta] = useState("");

  // 📄 paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/comprobantes");
      dispatch({ type: "LOAD", payload: res.data });
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (item) => {
    setSelectedItem(item);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedItem(null);
  };

  // 🧠 FIX FECHA
  const formatFecha = (fecha) => {
    if (!fecha) return "-";
    return fecha.replace("T", " ").slice(0, 16);
  };

  // 🔍 FILTROS
  const dataFiltrada = data.filter((item) => {
    // estado
    if (filtroEstado !== "TODOS" && item.estado !== filtroEstado) {
      return false;
    }

    // búsqueda
    const texto = search.toLowerCase();
    const coincideBusqueda =
      item.contact?.name?.toLowerCase().includes(texto) ||
      item.numero_referencia?.toLowerCase().includes(texto);

    if (!coincideBusqueda) return false;

    // 📅 filtro fecha REGISTRO
    if (fechaRegDesde || fechaRegHasta) {
      const fechaItem = item.createdAt?.slice(0, 10);

      if (fechaRegDesde && fechaItem < fechaRegDesde) return false;
      if (fechaRegHasta && fechaItem > fechaRegHasta) return false;
    }

    // 📄 filtro fecha COMPROBANTE
    if (fechaCompDesde || fechaCompHasta) {
      const fechaItem = item.fecha_comprobante?.slice(0, 10);

      if (fechaCompDesde && fechaItem < fechaCompDesde) return false;
      if (fechaCompHasta && fechaItem > fechaCompHasta) return false;
    }

    return true;
  });

  // 📄 PAGINACIÓN
  const dataPaginada = dataFiltrada.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>📄 Comprobantes</Title>

        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" onClick={fetchData}>
            Actualizar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* 🔍 FILTROS */}
      <div style={{ display: "flex", gap: 10, padding: 10, flexWrap: "wrap" }}>

        <input
          type="text"
          placeholder="Buscar cliente o referencia..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />

        <select
          value={filtroEstado}
          onChange={(e) => {
            setFiltroEstado(e.target.value);
            setPage(0);
          }}
        >
          <option value="TODOS">Todos</option>
          <option value="PENDIENTE">Pendientes</option>
          <option value="COMPLETADO">Completados</option>
          <option value="RECHAZADO">Rechazados</option>
        </select>

        {/* 📅 REGISTRO */}
        <label>Registro desde:</label>
        <input type="date" value={fechaRegDesde} onChange={(e) => setFechaRegDesde(e.target.value)} />

        <label>hasta:</label>
        <input type="date" value={fechaRegHasta} onChange={(e) => setFechaRegHasta(e.target.value)} />

        {/* 📄 COMPROBANTE */}
        <label>Comprobante desde:</label>
        <input type="date" value={fechaCompDesde} onChange={(e) => setFechaCompDesde(e.target.value)} />

        <label>hasta:</label>
        <input type="date" value={fechaCompHasta} onChange={(e) => setFechaCompHasta(e.target.value)} />
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Documento</TableCell>
              <TableCell>Contacto</TableCell>
              <TableCell>Banco</TableCell>
              <TableCell>Referencia</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Fecha Registro</TableCell>
              <TableCell>Fecha Comprobante</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {dataPaginada.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Tooltip title="Ver comprobante">
                    <IconButton onClick={() => handleOpenModal(item)}>
                      <DescriptionIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>

                <TableCell>{item.contact?.name || "-"}</TableCell>
                <TableCell>{item.banco || "-"}</TableCell>
                <TableCell>{item.numero_referencia || "-"}</TableCell>

                <TableCell>
                  {item.valor
                    ? `$${Number(item.valor).toLocaleString()}`
                    : "-"}
                </TableCell>

                <TableCell>{formatFecha(item.createdAt)}</TableCell>
                <TableCell>{formatFecha(item.fecha_comprobante)}</TableCell>

                <TableCell>
                  {item.estado === "COMPLETADO" && "✅ Completado"}
                  {item.estado === "PENDIENTE" && "⏳ Pendiente"}
                  {item.estado === "RECHAZADO" && "❌ Rechazado"}
                </TableCell>
              </TableRow>
            ))}

            {loading && <TableRowSkeleton columns={8} />}
          </TableBody>
        </Table>

        {/* PAGINACIÓN */}
        <div style={{ display: "flex", justifyContent: "space-between", padding: 10 }}>
          <div>
            <button disabled={page === 0} onClick={() => setPage(page - 1)}>⬅ Anterior</button>
            <span style={{ margin: "0 10px" }}>Página {page + 1}</span>
            <button
              disabled={(page + 1) * rowsPerPage >= dataFiltrada.length}
              onClick={() => setPage(page + 1)}
            >
              Siguiente ➡
            </button>
          </div>

          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(parseInt(e.target.value));
              setPage(0);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </Paper>

      {/* MODAL */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>📄 Detalle del Comprobante</DialogTitle>

        <DialogContent>
          {selectedItem && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

              {selectedItem.ruta_imagen && (
                <img
                  src={`${import.meta.env.VITE_BACKEND_URL}/comprobantes/${selectedItem.ruta_imagen}`}
                  style={{ width: "100%", borderRadius: 10 }}
                />
              )}

              <Divider />

              <p><b>Contacto:</b> {selectedItem.contact?.name || "-"}</p>
              <p><b>Banco:</b> {selectedItem.banco || "-"}</p>
              <p><b>Referencia:</b> {selectedItem.numero_referencia || "-"}</p>
              <p><b>Valor:</b> {selectedItem.valor || "-"}</p>
              <p><b>Fecha Registro:</b> {formatFecha(selectedItem.createdAt)}</p>
              <p><b>Fecha Comprobante:</b> {formatFecha(selectedItem.fecha_comprobante)}</p>
              <p><b>Estado:</b> {selectedItem.estado || "-"}</p>

            </div>
          )}
        </DialogContent>
      </Dialog>
    </MainContainer>
  );
};

export default Comprobantes;